import { type FC, type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

import type {
  TaskEfficiencyReferenceType,
  TaskEfficiencyResponseItem,
} from '@/tsClient/servers/noodleApi/ApiDefinition/taskEfficiency';
import { type TaskEfficiencyItem, useTaskEfficiencies } from '@/tsClient/taskEfficiency';

type TaskEfficiencyContextValue = {
  /** Look up efficiency data by reference type and ID. Returns undefined while loading, null if not found. */
  getEfficiency: (
    referenceType: TaskEfficiencyReferenceType,
    referenceId: string,
  ) => TaskEfficiencyResponseItem | null | undefined;
  /** Whether the batch request is still loading. */
  isLoading: boolean;
};

const TaskEfficiencyContext = createContext<TaskEfficiencyContextValue | null>(null);

/** Consume pre-fetched task efficiency data from the nearest TaskEfficiencyProvider. Returns null if no provider exists. */
export const useTaskEfficiencyContext = (): TaskEfficiencyContextValue | null => useContext(TaskEfficiencyContext);

/** Props for TaskEfficiencyProvider. */
export type TaskEfficiencyProviderProps = {
  /** Building block references to batch-fetch efficiency data for. */
  items: TaskEfficiencyItem[];
  children: ReactNode;
};

/** Batch-fetches task efficiency data for multiple building blocks and distributes via context. */
const TaskEfficiencyProvider: FC<TaskEfficiencyProviderProps> = ({ items, children }) => {
  const { data, isLoading } = useTaskEfficiencies(items.length > 0 ? items : null);

  const getEfficiency = useCallback(
    (
      referenceType: TaskEfficiencyReferenceType,
      referenceId: string,
    ): TaskEfficiencyResponseItem | null | undefined => {
      if (!data) return undefined;
      return data[`${referenceType}:${referenceId}`] ?? null;
    },
    [data],
  );

  const value = useMemo(() => ({ getEfficiency, isLoading }), [getEfficiency, isLoading]);

  return <TaskEfficiencyContext.Provider value={value}>{children}</TaskEfficiencyContext.Provider>;
};

export default TaskEfficiencyProvider;
